import { Injectable } from '@angular/core';
import { RoomSelectionService } from '../../room-selection.service';
import { LeadTaskService } from '../../../lead-task.service';
import { ErrorListService } from '../../../../../reusable-components/error-list/error-list.service';
import { RoomExaminationService } from '../room-examination.service';
import {
  RoomExaminationResultInfo,
  RadioType,
  TvBroadcastType,
  TvSize,
  VideoSize,
} from '../../../../../examinationResult.model';

@Injectable()
export class TariffSelectionService {
  public readonly errorListService: ErrorListService;
  // FR-05
  private readonly radioCheckboxErrorMessage =
    'components.room-examination.tariff-radio-type-error';
  // FR-06
  private readonly radioStationErrorMessage =
    'components.room-examination.tariff-radio-station-error';
  // FR-07
  private readonly tvCheckboxErrorMessage =
    'components.room-examination.tariff-tv-type-error';
  // FR-08
  private readonly tvStationErrorMessage =
    'components.room-examination.tariff-tv-station-error';

  constructor(
    private roomSelectionService: RoomSelectionService,
    private roomExaminationService: RoomExaminationService,
    private leadTaskService: LeadTaskService,
  ) {
    this.errorListService =
      roomExaminationService.errorListServiceTariffSelection;
  }

  private getRoomDraftInfo(): RoomExaminationResultInfo {
    return this.roomSelectionService.getRoomExaminationInput().roomDraftCopy;
  }

  updateErrorListService(): void {
    // FR-05: radio active + no type selected
    if (this.getRadio() && this.getRadioType() === null) {
      this.errorListService.addError(this.radioCheckboxErrorMessage);
    } else {
      this.errorListService.removeError(this.radioCheckboxErrorMessage);
    }
    // FR-06: radio active + station empty (not required for not-determinable)
    if (this.getRadio() && this.getRadioType() !== 'not-determinable' && !this.getRadioStation()) {
      this.errorListService.addError(this.radioStationErrorMessage);
    } else {
      this.errorListService.removeError(this.radioStationErrorMessage);
    }
    // FR-07: tv active + no type selected
    if (this.getTv() && this.getTvType() === null) {
      this.errorListService.addError(this.tvCheckboxErrorMessage);
    } else {
      this.errorListService.removeError(this.tvCheckboxErrorMessage);
    }
    // FR-08: tv active + station empty (not required for not-determinable)
    if (this.getTv() && this.getTvType() !== 'not-determinable' && !this.getTvStation()) {
      this.errorListService.addError(this.tvStationErrorMessage);
    } else {
      this.errorListService.removeError(this.tvStationErrorMessage);
    }
  }

  getAudio(): boolean {
    return this.getRoomDraftInfo().tariffs.audio;
  }

  setAudio(checked: boolean) {
    this.getRoomDraftInfo().tariffs.audio = checked;
    this.updateErrorListService();
  }

  getRadio(): boolean {
    return this.getRoomDraftInfo().tariffs.radio;
  }

  setRadio(checked: boolean) {
    this.getRoomDraftInfo().tariffs.radio = checked;
    // FR-09: reset radioType to null on activation
    if (checked) {
      this.getRoomDraftInfo().tariffs.radioType = null;
    }
    this.updateErrorListService();
  }

  getRadioType(): RadioType | null {
    return this.getRoomDraftInfo().tariffs.radioType;
  }

  setRadioType(type: RadioType | null) {
    this.getRoomDraftInfo().tariffs.radioType = type;
    if (type === 'not-determinable') {
      this.getRoomDraftInfo().tariffs.radioStation = null;
    }
    this.updateErrorListService();
  }

  getRadioStation(): string | null {
    return this.getRoomDraftInfo().tariffs.radioStation;
  }

  setRadioStation(station: string | null) {
    this.getRoomDraftInfo().tariffs.radioStation = station;
    this.updateErrorListService();
  }

  getAudioOrRadio(): boolean {
    return this.getRoomDraftInfo().tariffs.audioOrRadio;
  }

  setAudioOrRadio(checked: boolean) {
    this.getRoomDraftInfo().tariffs.audioOrRadio = checked;
    this.updateErrorListService();
  }

  getVideo(): boolean {
    return this.getRoomDraftInfo().tariffs.video;
  }

  setVideo(checked: boolean) {
    this.getRoomDraftInfo().tariffs.video = checked;
    this.updateErrorListService();
  }

  getVideoSize(): VideoSize {
    return this.getRoomDraftInfo().tariffs.videoSize;
  }

  setVideoSize(size: VideoSize) {
    this.getRoomDraftInfo().tariffs.videoSize = size;
    this.updateErrorListService();
  }

  getTv(): boolean {
    return this.getRoomDraftInfo().tariffs.tv;
  }

  setTv(checked: boolean) {
    this.getRoomDraftInfo().tariffs.tv = checked;
    // FR-10: reset tvType to null on activation
    if (checked) {
      this.getRoomDraftInfo().tariffs.tvType = null;
    }
    this.updateErrorListService();
  }

  getTvType(): TvBroadcastType | null {
    return this.getRoomDraftInfo().tariffs.tvType;
  }

  setTvType(type: TvBroadcastType | null) {
    this.getRoomDraftInfo().tariffs.tvType = type;
    if (type === 'not-determinable') {
      this.getRoomDraftInfo().tariffs.tvStation = null;
    }
    this.updateErrorListService();
  }

  getTvStation(): string | null {
    return this.getRoomDraftInfo().tariffs.tvStation;
  }

  setTvStation(station: string | null) {
    this.getRoomDraftInfo().tariffs.tvStation = station;
    this.updateErrorListService();
  }

  getTvSize(): TvSize {
    return this.getRoomDraftInfo().tariffs.tvSize;
  }

  setTvSize(size: TvSize) {
    this.getRoomDraftInfo().tariffs.tvSize = size;
    this.updateErrorListService();
  }

  getTvQuantity(): number | null {
    return this.getRoomDraftInfo().tariffs.tvQuantity;
  }

  setTvQuantity(quantity: number) {
    this.getRoomDraftInfo().tariffs.tvQuantity = quantity;
    this.updateErrorListService();
  }
}
